import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import Icons from '../components/common/Icons';

/* ------------------------------------------------------------------ */
/*  Animated Counter Hook (same pattern as HomePage)                    */
/* ------------------------------------------------------------------ */
function useCountUp(end, duration = 2000, start = 0, trigger = false) {
  const [value, setValue] = useState(start);
  useEffect(() => {
    if (!trigger || !end) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start, trigger]);
  return value;
}

/* ------------------------------------------------------------------ */
/*  Tab config                                                          */
/* ------------------------------------------------------------------ */
const tabs = [
  { key: 'csr', label: 'CSR Partners', icon: <Icons.Building /> },
  { key: 'agencies', label: 'Plantation Agencies', icon: <Icons.Gear /> },
  { key: 'individuals', label: 'Individuals', icon: <Icons.User /> },
  { key: 'volunteers', label: 'Volunteers', icon: <Icons.HandsRaised /> },
];

/* ---- Empty default when API returns nothing ---- */
const EMPTY_DATA = {
  summary: { total_csr: 0, total_agencies: 0, total_individuals: 0, total_volunteers: 0, total_plants: 0 },
  csr: [],
  agencies: [],
  individuals: [],
  volunteers: [],
};

/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
export default function ContributorsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('csr');

  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  /* ---- Fetch data ---- */
  useEffect(() => {
    document.title = 'Contributors | Haritanjali';
    publicAPI.getContributors()
      .then(res => {
        const d = res.data;
        setData(d && d.summary ? d : EMPTY_DATA);
      })
      .catch(() => setData(EMPTY_DATA))
      .finally(() => setLoading(false));
  }, []);

  /* ---- Stats visibility for counter animation ---- */
  useEffect(() => {
    if (!data || loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [data, loading]);

  /* ---- Scroll-based fade-in for .fade-section ---- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, activeTab]);

  /* ---- Data helpers ---- */
  const summary = data?.summary || {};
  const tabCounts = {
    csr: summary.total_csr || 0,
    agencies: summary.total_agencies || 0,
    individuals: summary.total_individuals || 0,
    volunteers: summary.total_volunteers || 0,
  };
  const currentList = data?.[activeTab] || [];
  const fmt = (n) => (n || 0).toLocaleString('en-IN');

  /* ---- Animated counters ---- */
  const csrCount = useCountUp(summary.total_csr || 0, 1800, 0, statsVisible);
  const agencyCount = useCountUp(summary.total_agencies || 0, 1800, 0, statsVisible);
  const indivCount = useCountUp(summary.total_individuals || 0, 1800, 0, statsVisible);
  const volCount = useCountUp(summary.total_volunteers || 0, 1800, 0, statsVisible);
  const totalTrees = useCountUp(summary.total_plants || 0, 2200, 0, statsVisible);

  /* ================================================================ */
  /*  CARD RENDERERS                                                    */
  /* ================================================================ */

  const renderCSRCard = (c, i) => (
    <div
      key={c.id}
      className="contrib-card-enhanced fade-section"
      style={{ ...cardBase, transitionDelay: `${i * 0.08}s` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
          border: '2px solid #c5a236', background: '#fff8e1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#c5a236',
        }}>
          <Icons.Building />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #c5a236, #d4b44a)',
            color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px',
            borderRadius: 10, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
          }}>CSR Partner</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20', lineHeight: 1.3 }}>
            {c.organisation_name}
          </div>
          {(c.city || c.state) && (
            <div style={{ fontSize: 13, color: '#999', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icons.MapPin /> {[c.city, c.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <div style={statBoxStyle}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#2e7d32' }}>{fmt(c.total_plants)}</div>
          <div style={statLabelStyle}>Trees Contributed</div>
        </div>
        <div style={statBoxStyle}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#2e7d32' }}>{fmt(c.project_count)}</div>
          <div style={statLabelStyle}>Projects</div>
        </div>
      </div>
      <Link to={`/contributors/${c.id}`} style={knowMoreStyle}>
        Know More <span style={{ fontSize: 18 }}>&rarr;</span>
      </Link>
    </div>
  );

  const renderAgencyCard = (a, i) => (
    <div
      key={a.id}
      className="contrib-card-enhanced fade-section"
      style={{ ...cardBase, transitionDelay: `${i * 0.08}s` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
          border: '2px solid #2e7d32', background: '#e8f5e9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#2e7d32',
        }}>
          <Icons.Gear />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #2e7d32, #43a047)',
            color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px',
            borderRadius: 10, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
          }}>Plantation Agency</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20', lineHeight: 1.3 }}>
            {a.organisation_name}
          </div>
          {a.areas_of_operation && (
            <div style={{ fontSize: 13, color: '#999', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icons.MapPin /> {a.areas_of_operation}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        <div style={statBoxStyle}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#2e7d32' }}>{fmt(a.total_trees_planted)}</div>
          <div style={statLabelStyle}>Trees Planted</div>
        </div>
        <div style={statBoxStyle}>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#2e7d32' }}>{fmt(a.project_count)}</div>
          <div style={statLabelStyle}>Projects</div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, fontSize: 13, color: '#666' }}>
        <span>{a.years_of_experience} years experience</span>
        {a.rating > 0 && (
          <span style={{ color: '#c5a236', fontWeight: 700 }}>
            &#9733; {a.rating.toFixed(1)}
          </span>
        )}
      </div>
      <Link to={`/contributors/${a.id}`} style={knowMoreStyle}>
        Know More <span style={{ fontSize: 18 }}>&rarr;</span>
      </Link>
    </div>
  );

  const renderIndividualCard = (ind, i) => (
    <div
      key={ind.id}
      className="contrib-card-enhanced fade-section"
      style={{ ...cardBase, transitionDelay: `${i * 0.08}s` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
          border: '2px solid #ff9800', background: '#fff3e0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#ff9800', overflow: 'hidden',
        }}>
          <Icons.AvatarPlaceholder />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #ff9800, #ffa726)',
            color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px',
            borderRadius: 10, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
          }}>Individual</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20' }}>{ind.name}</div>
          {(ind.city || ind.state) && (
            <div style={{ fontSize: 13, color: '#999', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icons.MapPin /> {[ind.city, ind.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      </div>
      <div style={{
        background: 'linear-gradient(135deg, #e8f5e9, #f1f8e9)',
        borderRadius: 14, padding: '18px 16px', textAlign: 'center', marginBottom: 16,
      }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: '#2e7d32' }}>{fmt(ind.trees_sponsored)}</div>
        <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>Trees Sponsored</div>
      </div>
      <Link to={`/contributors/${ind.id}`} style={knowMoreStyle}>
        Know More <span style={{ fontSize: 18 }}>&rarr;</span>
      </Link>
    </div>
  );

  const renderVolunteerCard = (vol, i) => (
    <div
      key={vol.id}
      className="contrib-card-enhanced fade-section"
      style={{ ...cardBase, transitionDelay: `${i * 0.08}s` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
          border: '2px solid #4caf50', background: '#e8f5e9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#4caf50', overflow: 'hidden',
        }}>
          <Icons.AvatarPlaceholder />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'inline-block', background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
            color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px',
            borderRadius: 10, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6,
          }}>Volunteer</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20' }}>{vol.name}</div>
          {(vol.city || vol.state) && (
            <div style={{ fontSize: 13, color: '#999', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icons.MapPin /> {[vol.city, vol.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      </div>
      <div style={{
        background: 'linear-gradient(135deg, #e8f5e9, #f1f8e9)',
        borderRadius: 14, padding: '18px 16px', textAlign: 'center',
      }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: '#2e7d32' }}>{fmt(vol.activities_count)}</div>
        <div style={{ fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>Activities Participated</div>
      </div>
    </div>
  );

  const renderCard = (item, index) => {
    if (activeTab === 'csr') return renderCSRCard(item, index);
    if (activeTab === 'agencies') return renderAgencyCard(item, index);
    if (activeTab === 'individuals') return renderIndividualCard(item, index);
    return renderVolunteerCard(item, index);
  };

  /* ================================================================ */
  /*  RENDER                                                            */
  /* ================================================================ */
  return (
    <>
      {/* ── Global Styles ── */}
      <style>{`
        .fade-section {
          opacity: 0; transform: translateY(40px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .fade-visible { opacity: 1; transform: translateY(0); }
        .contrib-card-enhanced {
          transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
          border: 2px solid transparent;
          cursor: default;
        }
        .contrib-card-enhanced:hover {
          transform: translateY(-8px) !important;
          box-shadow: 0 16px 40px rgba(46,125,50,0.15) !important;
          border-color: #2e7d32 !important;
        }
        @media (max-width: 640px) {
          .contrib-tabs-wrap { gap: 8px !important; }
          .contrib-tabs-wrap button { font-size: 13px !important; padding: 9px 18px !important; }
        }
      `}</style>

      {/* ==================== HERO ==================== */}
      <section style={{
        position: 'relative',
        minHeight: '45vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundImage: 'url(/images/hands-seedling.jpg)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
        textAlign: 'center', padding: '120px 20px 60px',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(27,94,32,0.7) 100%)',
        }} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 800 }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            color: '#c5a236', fontWeight: 600, marginBottom: 16, opacity: 0.95,
          }}>
            MAKING A DIFFERENCE
          </p>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800,
            color: '#fff', lineHeight: 1.12, margin: '0 0 16px',
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}>
            Our Contributors
          </h1>
          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)', color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.7, margin: 0, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Celebrating the organizations, agencies, individuals, and volunteers driving India's green revolution
          </p>
        </div>
      </section>

      {/* ==================== IMPACT STATS (Gold Circle Icons) ==================== */}
      {!loading && data && (
        <section ref={statsRef} style={{ padding: '80px 20px', background: '#fff', textAlign: 'center' }}>
          <div className="fade-section" style={{ marginBottom: 50 }}>
            <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
              Community Impact
            </p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 12 }}>
              Growing Together
            </h2>
            <p style={{ color: '#555', maxWidth: 600, margin: '0 auto', fontSize: 17 }}>
              Our collective strength in numbers
            </p>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'center',
            gap: 'clamp(16px, 3vw, 40px)', maxWidth: 1000,
            margin: '0 auto', flexWrap: 'wrap',
          }}>
            {[
              { icon: <Icons.Building />, value: csrCount, label: 'CSR Partners' },
              { icon: <Icons.Gear />, value: agencyCount, label: 'Agencies' },
              { icon: <Icons.User />, value: indivCount, label: 'Individuals' },
              { icon: <Icons.HandsRaised />, value: volCount, label: 'Volunteers' },
              { icon: <Icons.Tree />, value: totalTrees, label: 'Total Trees', suffix: '+' },
            ].map((m, i) => (
              <div
                key={i}
                className="fade-section"
                style={{ textAlign: 'center', flex: '0 0 auto', minWidth: 120, transitionDelay: `${i * 0.08}s` }}
              >
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  border: '2px solid #c5a236',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px', color: '#7a6c2a',
                }}>
                  {m.icon}
                </div>
                <div style={{
                  fontSize: 'clamp(24px, 2.5vw, 34px)', fontWeight: 800,
                  color: '#1b5e20', lineHeight: 1.1, marginBottom: 6,
                }}>
                  {m.value.toLocaleString()}{m.suffix || ''}
                </div>
                <div style={{ color: '#888', fontSize: 14, fontWeight: 400 }}>{m.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ==================== TAB PILLS ==================== */}
      <section style={{ padding: '48px 20px 0', background: '#f5f7f5' }}>
        <div className="contrib-tabs-wrap" style={{
          maxWidth: 1200, margin: '0 auto', display: 'flex', flexWrap: 'wrap',
          gap: 12, justifyContent: 'center',
        }}>
          {tabs.map(t => {
            const active = activeTab === t.key;
            return (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                background: active ? 'linear-gradient(135deg, #2e7d32, #4caf50)' : '#fff',
                color: active ? '#fff' : '#555',
                border: 'none', borderRadius: 30, padding: '11px 28px',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                boxShadow: active ? '0 4px 16px rgba(46,125,50,0.3)' : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {t.label} ({tabCounts[t.key]})
              </button>
            );
          })}
        </div>
      </section>

      {/* ==================== CARD GRID ==================== */}
      <section style={{ padding: '40px 20px 80px', background: '#f5f7f5' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#888', fontSize: 17 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                border: '4px solid #e0e0e0', borderTopColor: '#2e7d32',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
              }} />
              Loading contributors...
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          ) : currentList.length === 0 ? (
            <div className="fade-section" style={{ textAlign: 'center', padding: 60 }}>
              <div style={{
                width: 100, height: 100, borderRadius: '50%', margin: '0 auto 20px',
                background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#2e7d32',
              }}>
                <Icons.Tree />
              </div>
              <h3 style={{ color: '#1b5e20', fontSize: 24, fontWeight: 700, margin: '0 0 10px' }}>No Contributors Yet</h3>
              <p style={{ color: '#888', fontSize: 16, marginBottom: 28 }}>
                Be the first to contribute to India's green mission!
              </p>
              <Link to="/register" style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #2e7d32, #4caf50)',
                color: '#fff', padding: '14px 36px', borderRadius: 30,
                fontSize: 15, fontWeight: 600, textDecoration: 'none',
                boxShadow: '0 4px 16px rgba(46,125,50,0.3)',
              }}>Register to Contribute</Link>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 24,
            }}>
              {currentList.map((item, index) => renderCard(item, index))}
            </div>
          )}
        </div>
      </section>

      {/* ==================== CTA SECTION ==================== */}
      <section style={{
        position: 'relative', padding: '80px 20px', textAlign: 'center',
        backgroundImage: 'url(/images/canopy-lookup.jpg)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(27,94,32,0.88) 0%, rgba(0,0,0,0.7) 100%)',
        }} />
        <div className="fade-section" style={{ position: 'relative', zIndex: 2, maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 800,
            color: '#fff', margin: '0 0 16px', lineHeight: 1.15,
          }}>
            Join Our Growing Community
          </h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.88)', lineHeight: 1.7, marginBottom: 36 }}>
            Whether you're a corporation, agency, individual, or volunteer &mdash; every contribution counts towards a greener India.
          </p>
          <Link to="/register" style={{
            display: 'inline-block', background: '#ff9800', color: '#fff',
            padding: '16px 44px', borderRadius: 50, fontSize: 17, fontWeight: 700,
            textDecoration: 'none', boxShadow: '0 6px 24px rgba(255,152,0,0.4)',
            transition: 'transform 0.3s',
          }}>
            Register Now
          </Link>
        </div>
      </section>
    </>
  );
}

/* ── Shared styles ── */
const cardBase = {
  background: '#fff',
  borderRadius: 16,
  padding: 28,
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
};

const statBoxStyle = {
  background: '#f5f9f5', borderRadius: 12, padding: '14px 16px', flex: 1, textAlign: 'center',
};

const statLabelStyle = {
  fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4,
};

const knowMoreStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 14, fontWeight: 600, color: '#2e7d32',
  textDecoration: 'none', marginTop: 'auto',
  transition: 'color 0.2s',
};
